import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CRM from './CRM';
import Clients from './Clients';
import Services from './Services';
import Tasks from './Tasks';
import Finance from './Finance';
import Settings from './Settings';
import Reports from './Reports';
import Accounting from './Accounting';
import Workspace from './Workspace';
import { usePermissions } from '../hooks/usePermissions';
import ChanakyaXAI from '../components/ChanakyaXAI';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── ChanakyaX Logo (inline SVG — zero PNG dependency, dark mode safe) ────────
function ChanakyaXLogo({ mode = 'full', height = 38, darkMode = false }) {
  if (mode === 'icon') {
    // Collapsed sidebar — orange emblem only, no background
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="11000 0 4263 2800"
        height={height} width="auto" style={{ display: 'block' }} aria-label="ChanakyaX">
        <path fill="#FC5C0B" d="M13821.58 1767.69c29.82,-67.16 129.86,-166.84 176.62,-225.85l1265.19 -1541.84c-72.91,44.24 -958.38,861.93 -1046.05,951.47 -73.2,74.76 -133.38,130.95 -207.85,196.97 -115.46,102.34 -331.11,359.88 -403.04,398.54 -109.82,-138.98 -440.05,-452.63 -508.09,-566.8l-376.95 -0.81c27.87,74.49 129.25,154.75 180.19,212.36l525.64 573.92c-20.18,52.59 -238.08,304.7 -284.77,358.39 -89.85,103.32 -827.29,975.23 -858.4,1042.22l894.31 -685.24c166.19,-124.82 334.53,-406.43 445.38,-488.22 108.99,111.67 205.94,209.69 294.46,317.91 102.93,125.84 97.41,114.34 193.31,113.42 96.42,-0.92 210.43,3.99 304.07,-3.75 -32.27,-73.18 -545.13,-557.76 -594.04,-652.69z"/>
      </svg>
    );
  }
  // Full horizontal lockup — wordmark adapts to dark mode, orange stays orange
  const wordmarkColor = darkMode ? '#f1f5f9' : '#02124E';
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15263.4 3560.32"
      height={height} width="auto" style={{ display: 'block', maxWidth: 172 }} aria-label="ChanakyaX">
      <g>
        {/* Orange emblem — always brand orange regardless of mode */}
        <path fill="#FC5C0B" d="M13821.58 1767.69c29.82,-67.16 129.86,-166.84 176.62,-225.85l1265.19 -1541.84c-72.91,44.24 -958.38,861.93 -1046.05,951.47 -73.2,74.76 -133.38,130.95 -207.85,196.97 -115.46,102.34 -331.11,359.88 -403.04,398.54 -109.82,-138.98 -440.05,-452.63 -508.09,-566.8l-376.95 -0.81c27.87,74.49 129.25,154.75 180.19,212.36l525.64 573.92c-20.18,52.59 -238.08,304.7 -284.77,358.39 -89.85,103.32 -827.29,975.23 -858.4,1042.22l894.31 -685.24c166.19,-124.82 334.53,-406.43 445.38,-488.22 108.99,111.67 205.94,209.69 294.46,317.91 102.93,125.84 97.41,114.34 193.31,113.42 96.42,-0.92 210.43,3.99 304.07,-3.75 -32.27,-73.18 -545.13,-557.76 -594.04,-652.69z"/>
        {/* Wordmark — color driven by darkMode prop */}
        <path fill={wordmarkColor} d="M6267.05 2408.83l259.02 7.13 -3.47 -1277.96 -239.85 1.58 -8.4 890.95 -16.86 -19.07c-74.72,-55.17 -653.64,-655.92 -762.6,-768.93 -138.86,-144.03 -123.9,-147.95 -369.32,-128.47l5.46 1302.11c68.73,3.49 191.77,16.85 241.05,-9.12 32.6,-60.92 12.59,-741.84 10.3,-872.68l884.66 874.46z"/>
        <polygon fill={wordmarkColor} points="1973.31,1919.85 2787.18,1919.55 2789.06,2417.2 3052.41,2415.02 3052.05,1125.45 2788.64,1125.1 2782.22,1675.03 1971.99,1674.38 1969.16,1123.99 1708.58,1129.82 1712.66,2417.23 1970.19,2417.72"/>
        <path fill={wordmarkColor} d="M1350.08 1370.5c28.93,-41.1 106.82,-185.93 122.92,-246.2 -246.97,4.69 -809.94,-30.26 -1011.86,29.08 -586.87,172.47 -617.64,1004.21 -69,1213.44 207.71,79.21 701.49,56.65 959.06,51.46l115.82 -245.71c-258.8,-0.16 -524.78,4.7 -782.73,-1.19 -226.75,-5.18 -396.19,-136.09 -410.93,-350.47 -39.9,-580.06 603.74,-444.05 1076.71,-450.4z"/>
        <path fill={wordmarkColor} d="M7553.12 1462.41c83.47,89.42 489.4,901.38 543.34,959.24l303.59 -3.29c-22.67,-76.7 -325.16,-570 -389.75,-689.65 -67.82,-125.63 -128.68,-229.39 -196.06,-348.27 -111.53,-196.8 -123.17,-271.27 -261.57,-267.55 -128.5,3.45 -143.29,92.16 -255.3,288.54 -67.31,118.01 -127.22,221.6 -193.69,336.86 -57.14,99.08 -363.56,612.02 -382.04,678.56l295.5 0.76 535.98 -955.19z"/>
        <path fill={wordmarkColor} d="M11127.33 2419.38c307.64,0.45 285.66,31 349.1,-91.64 52.53,-101.57 447.59,-816.68 486.36,-857.52 2.39,2.67 5.45,6.42 6.89,8.26l263.55 464.85c88.71,159.81 172.38,318.09 260.87,477.95l311.25 1.16c-31.87,-100.64 -139.79,-252.36 -194.3,-353.77l-391.74 -683.03c-112.09,-189.45 -115.2,-272.65 -256.09,-269.11 -125.13,3.14 -151.35,110.39 -254.62,285.46l-394.12 680.47c-44.51,75.8 -168.52,269.27 -187.15,336.93z"/>
        <path fill={wordmarkColor} d="M3557.75 2417.64c62.19,-70.09 444.98,-834.11 543.16,-953.52 94.01,159.02 179.06,321.57 269.7,483.62l264.31 472.35 300.47 -3.46c-29.48,-85.74 -136.68,-253.64 -188.52,-341.7l-390.84 -683.98c-103.58,-172.88 -115.74,-274.6 -251.48,-275.12 -108.04,-0.42 -119.9,31.11 -165.28,110.37 -114.63,200.17 -662.8,1132.3 -670.68,1191.01l289.16 0.44z"/>
        <path fill={wordmarkColor} d="M10842.51 2074.77c-0.04,-97.1 -14.37,-112.55 36.47,-176.79 79.76,-100.78 596.47,-735.04 608.4,-773.2l-311.54 0.68 -463.6 594.6 -461.09 -595.7 -314.62 -1.81 184.17 239.83c66.21,84.11 117,143.75 178.86,225.86 58.35,77.43 123.48,160.78 183.7,235.15 106.16,131.1 93.25,91.15 93.24,307.19l3.43 291.02 261.89 -4.17 0.66 -342.68z"/>
        <path fill={wordmarkColor} d="M8601.33 2423.64l259.43 -8.84 -0.03 -649c46.99,18.06 178.25,162.93 228.46,209.5l441.53 423.84c62.04,43.21 304.78,15.89 404.37,18.81l-541.56 -509.63c-50.14,-42.1 -142.36,-121.85 -179.64,-168.55 94.49,-110.09 606.98,-577.77 636.47,-624.86 -109.55,-8.78 -302.21,-27.01 -369.2,27.67 -139.93,114.23 -515.97,533.52 -619.31,595.98l-2.18 -626.4 -258.97 -0.43 0.63 1311.9z"/>
        {/* Orange accent marks on letters */}
        <path fill="#F55C0C" d="M11809.25 2410.22c60.65,-79 85.99,-178.99 158.61,-238.89l131.75 243.68 134.65 -1.31c-20.99,-85.95 -95.34,-194.38 -132.9,-267.72 -28.92,-56.48 -96.86,-198.76 -138.57,-237.54 -43.16,54.49 -264.73,429.43 -284.18,507.24l130.64 -5.46z"/>
        <path fill="#FC610C" d="M3811.99 2412.45c147.13,8.7 122.98,19.87 182.24,-85.1 25.23,-44.7 62.35,-112.63 96.31,-154.19 40.97,26.47 128.95,203.5 145.35,242.85l141.64 -6.22 -269.27 -505.88c-47.55,31.35 -274.79,441.19 -296.26,508.53z"/>
        <path fill="#FC610C" d="M7416.67 2412.85c29.25,-68.43 99.16,-199.87 148.68,-240.16l140.32 242.37 126.98 -3.07c-45.56,-153.06 -203.91,-355.47 -264.24,-507.7l-15.79 15.2c-52.55,64.07 -130.35,186.84 -136.32,265.55l136.81 -0.95z" opacity="0.9"/>
        {/* Dot accents */}
        <path fill="#F8640A" d="M7312.89 3528.64c117.06,71.38 207.47,-28.14 142.27,-114.42 -44.93,-59.46 -185.39,-40.92 -142.27,114.42z"/>
      </g>
    </svg>
  );
}

// ─── Global Client Search ───────────────────────────────────────────────────
// Lives in the topbar so it's available from every module — matches
// Practive's "Search Clients" bar, letting a firm jump straight to a
// client's dashboard instead of opening the Clients list first.
function GlobalClientSearch({ darkMode, T }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/clients/search?q=${encodeURIComponent(query.trim())}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, BASE_URL]);

  const goToClient = (client) => {
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'clients', clientId: client.id } }));
    setQuery(''); setResults([]); setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: 280 }}>
      <div style={{ position: 'relative' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search Clients"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          style={{
            width: '100%', border: `1px solid ${T.border}`, borderRadius: 9,
            padding: '8px 12px 8px 34px', fontSize: 13, outline: 'none',
            background: T.bg, color: T.text, boxSizing: 'border-box',
          }}
        />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 340,
          background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12,
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
          zIndex: 9999, maxHeight: 360, overflowY: 'auto',
        }}>
          {loading ? (
            <p style={{ padding: '14px 16px', fontSize: 13, color: T.text3 }}>Searching…</p>
          ) : results.length === 0 ? (
            <p style={{ padding: '14px 16px', fontSize: 13, color: T.text3 }}>
              {query.trim() ? 'No clients found.' : 'Type to search clients by name, file no., PAN, GSTIN or mobile.'}
            </p>
          ) : (
            results.map(c => (
              <button key={c.id} onClick={() => goToClient(c)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                  padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                  borderBottom: `1px solid ${T.border}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0 }}>{c.client_name}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>
                    {c.file_number || '—'}{c.mobile ? ` · ${c.mobile}` : ''}
                  </p>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  color: c.is_active ? '#16a34a' : '#dc2626',
                  background: c.is_active ? (darkMode ? 'rgba(16,185,129,0.15)' : '#f0fdf4') : (darkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                }}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const [activePage, setActivePage] = useState('dashboard');
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const sidebarOpen = sidebarPinned || sidebarHovered;
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cx-theme') === 'dark');

  useEffect(() => {
    localStorage.setItem('cx-theme', darkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Theme tokens — used everywhere below
  const T = darkMode ? {
    bg:          '#0f172a',
    bg2:         '#1e293b',
    bg3:         '#334155',
    border:      '#334155',
    border2:     '#475569',
    text:        '#f1f5f9',
    text2:       '#94a3b8',
    text3:       '#64748b',
    cardBg:      '#1e293b',
    cardBorder:  '#334155',
    sidebarBg:   '#0f172a',
    sidebarBorder:'#1e293b',
    topbarBg:    '#0f172a',
    topbarBorder:'#1e293b',
    hoverBg:     'rgba(249,115,22,0.12)',
    hoverColor:  '#f97316',
    activeBg:    'linear-gradient(135deg,#0f1f45,#1a3a6b)',
    navLabel:    '#475569',
    pinBar:      '#94a3b8',
    versionBg:   'linear-gradient(135deg,#1e293b,#0f172a)',
    versionBorder:'#334155',
    inputBg:     '#1e293b',
    inputBorder: '#334155',
  } : {
    bg:          '#f1f5f9',
    bg2:         '#fff',
    bg3:         '#f8faff',
    border:      '#e8edf5',
    border2:     '#e2e8f0',
    text:        '#0f1f45',
    text2:       '#4a5568',
    text3:       '#94a3b8',
    cardBg:      '#fff',
    cardBorder:  '#f1f5f9',
    sidebarBg:   '#ffffff',
    sidebarBorder:'#e8edf5',
    topbarBg:    '#ffffff',
    topbarBorder:'#e8edf5',
    hoverBg:     'linear-gradient(135deg,#fff5eb,#ffeedd)',
    hoverColor:  '#e8620a',
    activeBg:    'linear-gradient(135deg,#0f1f45,#1a3a6b)',
    navLabel:    '#a0aec0',
    pinBar:      '#0f1f45',
    versionBg:   'linear-gradient(135deg,#f0f4ff,#fef3ec)',
    versionBorder:'#e8edf5',
    inputBg:     '#fff',
    inputBorder: '#e2e8f0',
  };

  // ── Notifications ────────────────────────────────────────────────────────
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showBellModal, setShowBellModal] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [bellRinging, setBellRinging] = useState(false);
  const prevUnreadRef = useRef(-1); // -1 = first load (don't trigger modal on mount)
  const notifToken = localStorage.getItem('token');
  const notifHeaders = { 'Authorization': `Bearer ${notifToken}` };

  const fetchNotifications = React.useCallback(async () => {
    try {
      const [countData, notifData] = await Promise.all([
        fetch(`${BASE}/api/notifications/unread-count`, { headers: notifHeaders }).then(r => r.json()).catch(() => ({ count: 0 })),
        fetch(`${BASE}/api/notifications`, { headers: notifHeaders }).then(r => r.json()).catch(() => []),
      ]);
      const newCount = countData.count || 0;
      const newNotifs = Array.isArray(notifData) ? notifData : [];

      // After first load, detect new unread assignment notifications
      if (prevUnreadRef.current >= 0 && newCount > prevUnreadRef.current) {
        setBellRinging(true);
        setTimeout(() => setBellRinging(false), 2000);
        const assignmentTypes = ['task_assignment', 'lead_assignment', 'client_assignment', 'task_approval', 'task_mention'];
        const freshTaskNotifs = newNotifs.filter(n => !n.is_read && assignmentTypes.includes(n.type));
        if (freshTaskNotifs.length > 0) {
          setShowBellModal(true);
        }
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
      setNotifications(newNotifs);
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 10000);
    // The 10s interval only actually fires every 10s while this tab is the
    // active/foreground one — browsers throttle background-tab timers (down
    // to roughly once a minute, sometimes less after several minutes hidden)
    // to save power. That's the real cause behind "a mention took ages to
    // show up": it wasn't slow, the timer just wasn't running while the tab
    // was in the background. Refreshing immediately on regaining focus closes
    // that gap — the moment someone switches back to (or alt-tabs into) this
    // tab, whatever piled up while it was hidden shows up right away instead
    // of waiting for the next throttled tick.
    const onFocusOrVisible = () => { if (document.visibilityState !== 'hidden') fetchNotifications(); };
    document.addEventListener('visibilitychange', onFocusOrVisible);
    window.addEventListener('focus', onFocusOrVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      window.removeEventListener('focus', onFocusOrVisible);
    };
  }, [fetchNotifications]);

  // ── Cross-component navigation ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { page, tab, section, clientId, taskId } = e.detail || {};
      if (!page) return;

      // Store extra context so target page can read it on mount
      if (tab)      localStorage.setItem('navTab',      tab);
      else          localStorage.removeItem('navTab');
      if (section)  localStorage.setItem('navSection',  section);
      else          localStorage.removeItem('navSection');
      if (clientId) localStorage.setItem('navClientId', String(clientId));
      else          localStorage.removeItem('navClientId');
      if (taskId)   localStorage.setItem('navTaskId',   String(taskId));
      else          localStorage.removeItem('navTaskId');

      setActivePage(page);
    };
    window.addEventListener('navigateTo', handler);
    return () => window.removeEventListener('navigateTo', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await fetch(`${BASE}/api/notifications/read-all`, { method: 'PUT', headers: notifHeaders });
      prevUnreadRef.current = 0;
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setShowBellModal(false);
      setShowNotifPanel(false);
    } catch (_) {}
  };

  const clearAll = async () => {
    try {
      await fetch(`${BASE}/api/notifications/read-all`, { method: 'PUT', headers: notifHeaders });
      prevUnreadRef.current = 0;
      setUnreadCount(0);
      setNotifications([]);
      setShowNotifPanel(false);
    } catch (_) {}
  };

  const markOneRead = async (id) => {
    try {
      await fetch(`${BASE}/api/notifications/${id}/read`, { method: 'PUT', headers: notifHeaders });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        prevUnreadRef.current = next;
        return next;
      });
    } catch (_) {}
  };

  const handleLogout = () => {
    fetch(`${BASE}/api/auth/logout`, { method: 'POST', headers: notifHeaders }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const { can, loading: permsLoading } = usePermissions();

  // activePage → role_permissions module key. Pages not listed (dashboard,
  // workspace) are always allowed; workspace does its own per-tile gating.
  const PAGE_MODULE = {
    crm: 'crm', clients: 'clients', services: 'services',
    tasks: 'tasks', finance: 'finance', settings: 'settings',
    reports: 'reports', accounting: 'accounting',
  };
  // Pages that now live in the Workspace tile grid and open in their own tab —
  // any internal shortcut (dashboard tiles, quick actions) should open the
  // tab instead of switching activePage.
  const WORKSPACE_ROUTES = { renewals: '/workspace/renewals', people: '/workspace/people' };
  const goToPage = (page) => {
    if (WORKSPACE_ROUTES[page]) { window.open(WORKSPACE_ROUTES[page], '_blank'); return; }
    setActivePage(page);
  };
  const SvgIcon = ({ d, d2, viewBox = '0 0 24 24', filled = false, color = 'currentColor' }) => (
    <svg width="18" height="18" viewBox={viewBox} fill={filled ? color : 'none'} stroke={filled ? 'none' : color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />{d2 && <path d={d2} />}
    </svg>
  );

  const allMenuItems = [
    { name: 'Dashboard',     path: 'dashboard', module: 'dashboard',
      icon: <SvgIcon d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" d2="M9 21V12h6v9" /> },
    { name: 'CRM',           path: 'crm',       module: 'crm',
      icon: <SvgIcon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" d2="M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /> },
    { name: 'Clients',       path: 'clients',   module: 'clients',
      icon: <SvgIcon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" d2="M12 11a4 4 0 100-8 4 4 0 000 8z" /> },
    { name: 'Services',      path: 'services',  module: 'services',
      icon: <SvgIcon d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /> },
    { name: 'Tasks',         path: 'tasks',     module: 'tasks',
      icon: <SvgIcon d="M9 11l3 3L22 4" d2="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /> },
    { name: 'Finance',       path: 'finance',   module: 'finance',
      icon: <SvgIcon d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /> },
    { name: 'Accounting',    path: 'accounting', module: 'accounting',
      icon: <SvgIcon d="M4 19.5A2.5 2.5 0 016.5 17H20" d2="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /> },
    { name: 'Reports',       path: 'reports',   module: 'reports',
      icon: <SvgIcon d="M18 20V10M12 20V4M6 20v-6" /> },
    { name: 'Workspace',     path: 'workspace', module: 'workspace',
      icon: <SvgIcon d="M3 6h18M3 12h18M3 18h18" d2="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" /> },
    { name: 'Client Portal', path: 'client-portal-external', module: 'clients', external: true,
      icon: <SvgIcon d="M10 13a5 5 0 007.07 0l1.93-1.93a5 5 0 00-7.07-7.07L10.5 5.43" d2="M14 11a5 5 0 00-7.07 0l-1.93 1.93a5 5 0 007.07 7.07L13.5 18.57" /> },
    { name: 'Settings',      path: 'settings',  module: 'settings',
      icon: <SvgIcon d="M12 15a3 3 0 100-6 3 3 0 000 6z" d2="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /> },
  ];
  
  // Workspace (MCA / HR / Referrals / future paid modules) always shows in the sidebar —
  // its real access control happens per-tab, after the click (firm subscription + per-user grant).
  const menuItems = allMenuItems.filter(item => item.module === 'workspace' || can(item.module, 'view'));

  const renderContent = () => {
    // Read and clear nav context on each render
    const navTab      = localStorage.getItem('navTab');
    const navSection  = localStorage.getItem('navSection');
    const navClientId = localStorage.getItem('navClientId');
    const navTaskId   = localStorage.getItem('navTaskId');

    // Permission guard — the sidebar hides links the user can't view, but the
    // page can still be reached via dashboard tiles, quick actions or
    // cross-component navigateTo events. Block the render here too.
    const needed = PAGE_MODULE[activePage];
    if (needed && !permsLoading && !can(needed, 'view')) {
      return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'70vh', textAlign:'center', padding:24 }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:'0 0 8px' }}>
              You don't have access to this module
            </h3>
            <p style={{ fontSize:13, color:T.text3, margin:'0 0 16px' }}>
              Ask your admin to grant it under Settings → User Roles.
            </p>
            <button onClick={() => setActivePage('dashboard')} style={{
              padding:'8px 18px', borderRadius:10, border:`1px solid ${T.border2}`,
              background:T.bg2, color:T.text, fontSize:13, fontWeight:600, cursor:'pointer',
            }}>Back to Dashboard</button>
          </div>
        </div>
      );
    }

    switch (activePage) {
      case 'crm':          return <CRM     initialTab={navTab} darkMode={darkMode} T={T} />;
      case 'clients':      return <Clients darkMode={darkMode} T={T} initialClientId={navClientId} />;
      case 'services':     return <Services darkMode={darkMode} T={T} />;
      case 'tasks':        return <Tasks darkMode={darkMode} T={T} />;
      case 'finance':      return <Finance darkMode={darkMode} T={T} />;
      case 'accounting':   return <Accounting darkMode={darkMode} T={T} />;
      case 'settings':     return <Settings initialSection={localStorage.getItem('navSection')} darkMode={darkMode} T={T} />;
      case 'reports':      return <Reports darkMode={darkMode} T={T} />;
      case 'workspace':    return <Workspace darkMode={darkMode} T={T} can={can} />;
      default:             return <DashboardHome setActivePage={goToPage} darkMode={darkMode} T={T} />;
    }
  };

  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: T.bg, display: 'flex' }}>

{/* SIDEBAR */}
      <style>{`
        .sidebar-nav::-webkit-scrollbar { display: none; }
        .sb-item { transition: all 0.18s ease; }
        .sb-item:hover { background: ${T.hoverBg} !important; color: ${T.hoverColor} !important; }
        .sb-item:hover .sb-icon { color: ${T.hoverColor} !important; }
        .sb-item-active { background: ${T.activeBg} !important; color: #fff !important; box-shadow: 0 4px 14px rgba(15,31,69,0.25); }
        .sb-item-active .sb-icon { color: #f97316 !important; }
        .sb-item-mca { color: #e8620a; }
        .lead-form-scroll::-webkit-scrollbar { display: none; }
        .sb-item-mca:hover { background: ${T.hoverBg} !important; }
        .sb-pin:hover { background: ${darkMode ? '#1e293b' : '#f1f5f9'} !important; }
        body { background: ${T.bg}; }
        .lead-form-scroll::-webkit-scrollbar { display: none; }
      `}</style>
      <div
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        className="flex flex-col fixed left-0 top-0 z-40"
        style={{
          width: sidebarOpen ? '232px' : '58px',
          height: '100vh',
          background: T.sidebarBg,
          borderRight: `1px solid ${T.sidebarBorder}`,
          boxShadow: sidebarOpen ? '4px 0 28px rgba(15,31,69,0.08)' : '2px 0 8px rgba(15,31,69,0.05)',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}>

        {/* TOP — logo & pin */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 10px', minHeight: 62,
          borderBottom: `1px solid ${T.sidebarBorder}`, flexShrink: 0,
        }}>
          {/* Collapsed: orange emblem only, centered */}
          {!sidebarOpen && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '0 4px' }}>
              <ChanakyaXLogo mode="icon" height={28} darkMode={darkMode} />
            </div>
          )}
          {/* Open: hamburger pin + full wordmark logo */}
          {sidebarOpen && (
            <>
              <button
                onClick={() => setSidebarPinned(!sidebarPinned)}
                className="sb-pin"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 5, padding: '7px 6px',
                  borderRadius: 8, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0,
                }}>
                <span style={{ display: 'block', width: 18, height: 2, background: T.pinBar, borderRadius: 2 }}></span>
                <span style={{ display: 'block', width: sidebarPinned ? 11 : 18, height: 2, background: T.pinBar, borderRadius: 2, transition: 'width 0.2s' }}></span>
                <span style={{ display: 'block', width: 18, height: 2, background: T.pinBar, borderRadius: 2 }}></span>
              </button>
              <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', flex: 1 }}>
                <ChanakyaXLogo mode="full" height={36} darkMode={darkMode} />
              </div>
            </>
          )}
        </div>

        {/* MENU */}
        <nav className="sidebar-nav" style={{
          flex: 1, padding: '10px 8px', minHeight: 0,
          overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {/* Section label */}
          {sidebarOpen && (
            <p style={{ fontSize: 9.5, fontWeight: 700, color: T.navLabel, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 10px 8px', margin: 0 }}>
              Navigation
            </p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {menuItems.map((item) => {
              const isActive = activePage === item.path;
              const isMca = item.path === 'workspace';
              return (
                <li key={item.name}>
                  <button
                    onClick={async () => {
                      if (item.external) {
                        // No hardcoded domain — a firm's own domain (once set up)
                        // wins if present, otherwise every firm always has the
                        // /portal/<slug>/login fallback on this app's own origin
                        // (see Settings → Client Portal, client_portal_settings).
                        try {
                          const t = localStorage.getItem('token');
                          const r = await fetch(`${BASE}/api/client-portal-settings`, { headers: { Authorization: `Bearer ${t}` } });
                          const d = await r.json();
                          const wildcardBase = process.env.REACT_APP_CHANAKYAX_BASE_DOMAIN;
                          if (d?.custom_domain) {
                            window.open(`https://${d.custom_domain}/`, '_blank', 'noopener,noreferrer');
                          } else if (d?.portal_slug && wildcardBase) {
                            window.open(`https://${d.portal_slug}.${wildcardBase}/`, '_blank', 'noopener,noreferrer');
                          } else if (d?.portal_slug) {
                            window.open(`${window.location.origin}/portal/${d.portal_slug}/login`, '_blank', 'noopener,noreferrer');
                          } else {
                            alert('Set your Client Portal link first: Settings → Client Settings → Client Portal.');
                            setActivePage('settings');
                            window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'settings', section: 'client-portal' } }));
                          }
                        } catch (e) {
                          alert('Could not load the Client Portal link. Try again from Settings → Client Portal.');
                        }
                        return;
                      }
                      // Direct sidebar click — drop any stale cross-nav context
                      // so the target page opens at its default view, not wherever
                      // a previous "navigateTo" deep-link last pointed.
                      localStorage.removeItem('navTab');
                      localStorage.removeItem('navSection');
                      localStorage.removeItem('navClientId');
                      localStorage.removeItem('navTaskId');
                      setActivePage(item.path);
                    }}
                    title={!sidebarOpen ? item.name : ''}
                    className={`sb-item w-full ${isActive ? 'sb-item-active' : ''} ${isMca ? 'sb-item-mca' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 11,
                      padding: sidebarOpen ? '9px 12px' : '9px 0',
                      justifyContent: sidebarOpen ? 'flex-start' : 'center',
                      borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: isActive ? undefined : 'none',
                      color: isActive ? '#fff' : isMca ? '#e8620a' : T.text2,
                      fontSize: 13.5, fontWeight: isActive ? 600 : 500,
                      width: '100%', textAlign: 'left',
                    }}>
                    {/* Icon wrapper */}
                    <span className="sb-icon" style={{
                      flexShrink: 0,
                      color: isActive ? '#f97316' : isMca ? '#e8620a' : T.text3,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 22,
                    }}>
                      {item.icon}
                    </span>
                    {sidebarOpen && (
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', flex: 1 }}>{item.name}</span>
                    )}
                    {sidebarOpen && isMca && (
                      <svg style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.6 }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    )}
                    {sidebarOpen && isActive && !isMca && (
                      <span style={{
                        marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%',
                        background: '#f97316', flexShrink: 0,
                      }}></span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* BOTTOM — version badge only */}
        <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, padding: '10px 8px', flexShrink: 0 }}>
          {sidebarOpen ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', borderRadius: 8,
              background: T.versionBg, border: `1px solid ${T.versionBorder}`,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                </svg>
              </div>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.text, margin: 0 }}>ChanakyaX ERP</p>
                <p style={{ fontSize: 10, color: T.text3, margin: 0 }}>v2.0 · Legal Chanakya</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ marginLeft: 58, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.bg, minHeight: 0 }}>
        {/* Topbar */}
        <div style={{
          background: T.topbarBg, borderBottom: `1px solid ${T.topbarBorder}`,
          padding: '0 24px', height: 56, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 30,
          boxShadow: darkMode ? '0 1px 0 rgba(255,255,255,0.05)' : '0 1px 4px rgba(15,31,69,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <h1 style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: 0, whiteSpace: 'nowrap' }}>
              {menuItems.find(m => m.path === activePage)?.name || 'Dashboard'}
            </h1>
            <GlobalClientSearch darkMode={darkMode} T={T} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Dark / Light Toggle */}
            <button
              onClick={() => setDarkMode(d => !d)}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              style={{
                width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.border}`,
                background: T.bg2, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#f97316'}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
            >
              {darkMode ? (
                /* Sun icon — shown in dark mode to switch to light */
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                /* Moon icon — shown in light mode to switch to dark */
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f1f45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>

            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowNotifPanel(v => !v); setShowBellModal(false); }}
                style={{
                  width: 36, height: 36, borderRadius: 10, border: `1px solid ${showNotifPanel?'#3b82f6':T.border}`,
                  background: showNotifPanel?'rgba(59,130,246,0.1)':T.bg2, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', position: 'relative',
                  color: showNotifPanel?'#3b82f6':T.text3, transition:'all 0.15s',
                }}
                onMouseEnter={e=>{ if(!showNotifPanel){ e.currentTarget.style.background=T.bg3; e.currentTarget.style.color=T.text2; }}}
                onMouseLeave={e=>{ if(!showNotifPanel){ e.currentTarget.style.background=T.bg2; e.currentTarget.style.color=T.text3; }}}
                title="Notifications">
                <svg
                  width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                  style={bellRinging ? { animation:'bell-ring 0.6s ease-in-out 3' } : {}}>
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    minWidth: unreadCount > 9 ? 20 : 18,
                    height: 18, background: '#ef4444',
                    borderRadius: 9, color: '#fff',
                    fontSize: unreadCount > 99 ? 8 : 10,
                    fontWeight: 700, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', padding: '0 4px', lineHeight: 1,
                    boxShadow: '0 0 0 2px white',
                    animation: bellRinging ? 'badge-blink 0.4s ease-in-out 4' : 'none',
                  }}>
                    {unreadCount > 100 ? '100+' : unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifPanel && (
                <NotificationPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  onMarkAllRead={markAllRead}
                  onMarkOneRead={markOneRead}
                  onClearAll={clearAll}
                  onClose={() => setShowNotifPanel(false)}
                  darkMode={darkMode}
                  T={T} />
              )}
            </div>

            <AvatarMenu user={user} onLogout={handleLogout} darkMode={darkMode} T={T} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>{renderContent()}</div>
      </div>

      {/* Bell Alert Modal — centered overlay for new task assignments */}
      {showBellModal && (
        <BellAlertModal
          notifications={notifications.filter(n => !n.is_read && ['task_assignment', 'lead_assignment', 'client_assignment', 'task_approval', 'task_mention'].includes(n.type))}
          onGotIt={markAllRead}
          onClose={() => setShowBellModal(false)} />
      )}
      <ChanakyaXAI module={activePage} isDark={darkMode} />
    </div>
  );
}

// ─── Dashboard Home ───────────────────────────────────────────────────────────
function DashboardHome({ setActivePage, darkMode, T }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user?.role || 'staff'; // 'admin' | 'manager' | 'staff'

  const [financeMonths, setFinanceMonths] = useState(6);
  const [stats, setStats] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [hrData, setHrData] = useState({ logged_in_today: [] });
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [financeData, setFinanceData] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState({});
  const [taskBilling, setTaskBilling] = useState({});
  const [loading, setLoading] = useState(true);
  const [showOfficeLog, setShowOfficeLog] = useState(false);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Load Chart.js from CDN once
  useEffect(() => {
    if (window.Chart) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const daysLeft = (date) => Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
  const fmtCurr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const todayLabel = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/dashboard/stats`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${BASE}/api/renewals/all`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/tasks`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/dashboard/hr-today`, { headers }).then(r => r.json()).catch(() => ({ logged_in_today: [] })),
      fetch(`${BASE}/api/dashboard/recent-leads`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/dashboard/recent-clients`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/dashboard/recent-payments`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/invoices/stats`, { headers }).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE}/api/dashboard/task-billing-stats`, { headers }).then(r => r.json()).catch(() => ({})),
    ]).then(([s, ren, tsk, hr, rl, rc, rp, is, tb]) => {
      setStats(s);
      setRenewals(Array.isArray(ren) ? ren : []);
      setTasks(Array.isArray(tsk) ? tsk : []);
      setHrData(hr || { logged_in_today: [] });
      setRecentLeads(Array.isArray(rl) ? rl : []);
      setRecentClients(Array.isArray(rc) ? rc : []);
      setRecentPayments(Array.isArray(rp) ? rp : []);
      setInvoiceStats(is || {});
      setTaskBilling(tb || {});
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/dashboard/finance-monthly?months=${financeMonths}`, { headers })
      .then(r => r.json())
      .then(data => setFinanceData(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [financeMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chart.js — rebuild whenever financeData or darkMode changes
  useEffect(() => {
    if (!chartRef.current || financeData.length === 0) return;
    if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; }
    const isDark = darkMode;
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tickColor = isDark ? '#64748b' : '#9ca3af';
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels: financeData.map(d => d.month_label),
        datasets: [
          {
            label: 'Billed',
            data: financeData.map(d => Number(d.total_billed) || 0),
            backgroundColor: isDark ? 'rgba(99,102,241,0.75)' : 'rgba(99,102,241,0.8)',
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: 'Collected',
            data: financeData.map(d => Number(d.total_collected) || 0),
            backgroundColor: isDark ? 'rgba(16,185,129,0.75)' : 'rgba(16,185,129,0.8)',
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ₹${Number(ctx.raw).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
            },
          },
        },
        scales: {
          x: {
            grid: { color: gridColor },
            ticks: { color: tickColor, font: { size: 11 } },
          },
          y: {
            grid: { color: gridColor },
            ticks: {
              color: tickColor, font: { size: 11 },
              callback: (v) => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v >= 1000 ? `₹${(v/1000).toFixed(0)}K` : `₹${v}`,
            },
            beginAtZero: true,
          },
        },
      },
    });
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; } };
  }, [financeData, darkMode]);

  const urgentRenewals = renewals.filter(r => daysLeft(r.expiry_date) <= 30);
  const pendingTasks   = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
  const overdueTasks   = tasks.filter(t => t.due_date && new Date(t.due_date) < now && t.status !== 'completed' && t.status !== 'cancelled');
  const myTasks        = tasks.filter(t => t.assigned_to === user?.id && t.status !== 'completed' && t.status !== 'cancelled');
  const totalTasks     = stats?.tasks?.total || 1;
  const completedPct   = Math.round(((stats?.tasks?.completed || 0) / totalTasks) * 100);

  // Unified action items sorted by urgency
  const actionItems = [
    ...overdueTasks.slice(0, 4).map(t => ({
      type: 'task', urgency: 'high', label: t.title,
      sub: t.client_name || 'No client', badge: 'Overdue', badgeColor: '#ef4444',
      page: 'tasks',
    })),
    ...urgentRenewals.slice(0, 4).map(r => ({
      type: 'renewal', urgency: daysLeft(r.expiry_date) < 0 ? 'high' : daysLeft(r.expiry_date) <= 7 ? 'med' : 'low',
      label: r.client_name, sub: r.renewal_type,
      badge: daysLeft(r.expiry_date) < 0 ? 'Expired' : `${daysLeft(r.expiry_date)}d left`,
      badgeColor: daysLeft(r.expiry_date) < 0 ? '#ef4444' : daysLeft(r.expiry_date) <= 7 ? '#f97316' : '#f59e0b',
      page: 'renewals',
    })),
    ...recentPayments.filter(p => p.outstanding > 0).slice(0, 3).map(p => ({
      type: 'invoice', urgency: 'low', label: p.client_name, sub: p.invoice_number,
      badge: 'Outstanding', badgeColor: '#6366f1', page: 'finance',
    })),
  ].sort((a, b) => (a.urgency === 'high' ? -1 : b.urgency === 'high' ? 1 : 0));

  // Role-gated KPI cards
  const kpiCards = role === 'staff' ? [
    { label: 'My Tasks', value: myTasks.length, sub: `${myTasks.filter(t=>t.status==='in_progress').length} in progress`, icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', accent: '#6366f1', page: 'tasks' },
    { label: 'Overdue', value: overdueTasks.filter(t=>t.assigned_to===user?.id).length, sub: 'needs attention', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', accent: '#ef4444', page: 'tasks' },
    { label: 'My Renewals', value: urgentRenewals.filter(r=>r.assigned_to===user?.id).length, sub: 'expiring <30 days', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', accent: '#f59e0b', page: 'renewals' },
    { label: 'Completed', value: tasks.filter(t=>t.assigned_to===user?.id&&t.status==='completed').length, sub: 'tasks done', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', accent: '#10b981', page: 'tasks' },
  ] : role === 'manager' ? [
    { label: 'Open Leads', value: stats?.leads?.open ?? '—', sub: `${stats?.leads?.conversionRate ?? 0}% conversion`, icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', accent: '#6366f1', page: 'crm' },
    { label: 'Pending Tasks', value: stats?.tasks?.pending ?? '—', sub: `${stats?.tasks?.overdue ?? 0} overdue`, icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', accent: '#f59e0b', page: 'tasks' },
    { label: 'Active Clients', value: stats?.clients?.active ?? '—', sub: `of ${stats?.clients?.total ?? 0} total`, icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', accent: '#3b82f6', page: 'clients' },
    { label: 'Renewals <30d', value: urgentRenewals.length, sub: `${renewals.filter(r=>daysLeft(r.expiry_date)<0).length} expired`, icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', accent: '#ef4444', page: 'renewals' },
    { label: 'Outstanding', value: fmtCurr(invoiceStats.total_outstanding), sub: `${invoiceStats.overdue ?? 0} overdue invoices`, icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', accent: '#f97316', page: 'finance' },
  ] : [
    // Admin — full picture
    { label: 'Billed MTD', value: fmtCurr(invoiceStats.total_value), sub: `${fmtCurr(invoiceStats.total_collected)} collected`, icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 20h16a2 2 0 002-2V8a2 2 0 00-2-2H8.5L4 8.5V18a2 2 0 002 2z', accent: '#6366f1', page: 'finance' },
    { label: 'Outstanding', value: fmtCurr(invoiceStats.total_outstanding), sub: `${invoiceStats.overdue ?? 0} overdue invoices`, icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', accent: '#ef4444', page: 'finance' },
    { label: 'Active Clients', value: stats?.clients?.active ?? '—', sub: `of ${stats?.clients?.total ?? 0} total`, icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', accent: '#3b82f6', page: 'clients' },
    { label: 'Open Leads', value: stats?.leads?.open ?? '—', sub: `${stats?.leads?.conversionRate ?? 0}% conversion rate`, icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', accent: '#8b5cf6', page: 'crm' },
    { label: 'Renewals <30d', value: urgentRenewals.length, sub: `${renewals.filter(r=>daysLeft(r.expiry_date)<0).length} already expired`, icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', accent: '#f97316', page: 'renewals' },
    { label: 'Unbilled Tasks', value: taskBilling.unbilled_count ?? '—', sub: `${taskBilling.billed_count ?? 0} billed · ${fmtCurr(taskBilling.pending_value)} pending`, icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', accent: '#8b5cf6', page: 'tasks' },
  ];

  // Module snapshot tiles
  const moduleTiles = [
    {
      key: 'crm', label: 'CRM', page: 'crm',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
      value: stats?.leads?.total ?? 0, valueLabel: 'total leads',
      sub: `${stats?.leads?.open ?? 0} open · ${stats?.leads?.converted ?? 0} won`,
      accent: '#6366f1', lightBg: '#eef2ff', darkBg: 'rgba(99,102,241,0.12)',
    },
    {
      key: 'clients', label: 'Clients', page: 'clients',
      icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
      value: stats?.clients?.active ?? 0, valueLabel: 'active',
      sub: `${stats?.clients?.total ?? 0} total clients`,
      accent: '#3b82f6', lightBg: '#eff6ff', darkBg: 'rgba(59,130,246,0.12)',
    },
    {
      key: 'tasks', label: 'Tasks', page: 'tasks',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      value: stats?.tasks?.pending ?? 0, valueLabel: 'pending',
      sub: `${stats?.tasks?.overdue ?? 0} overdue · ${stats?.tasks?.in_progress ?? 0} in progress`,
      accent: '#f59e0b', lightBg: '#fffbeb', darkBg: 'rgba(245,158,11,0.12)',
    },
    {
      key: 'renewals', label: 'Renewals', page: 'renewals',
      icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      value: urgentRenewals.length, valueLabel: 'due <30d',
      sub: `${renewals.filter(r=>daysLeft(r.expiry_date)<0).length} expired`,
      accent: '#ef4444', lightBg: '#fef2f2', darkBg: 'rgba(239,68,68,0.12)',
    },
    {
      key: 'finance', label: 'Finance', page: 'finance',
      icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      value: fmtCurr(invoiceStats.total_outstanding), valueLabel: 'outstanding',
      sub: `${fmtCurr(invoiceStats.total_collected)} collected`,
      accent: '#10b981', lightBg: '#f0fdf4', darkBg: 'rgba(16,185,129,0.12)',
    },
  ];

  const C = {
    card:      { background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 14 },
    text:      T.text, text2: T.text2, text3: T.text3,
    bg:        T.bg, bg2: T.bg2, bg3: T.bg3,
    border:    T.border,
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, flexDirection: 'column', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${T.border}`, borderTopColor: '#f97316', animation: 'spin 0.8s linear infinite' }}></div>
      <p style={{ color: T.text3, fontSize: 13 }}>Loading dashboard…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <>
    <div style={{ padding: '20px 24px 32px', background: T.bg, minHeight: '100%' }}>
      <style>{`
        .cx-dash-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important; }
        .cx-dash-kpi { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .cx-tile:hover { transform: translateY(-2px); }
        .cx-tile { transition: transform 0.18s ease, box-shadow 0.18s ease; cursor: pointer; }
        .cx-action-row:hover { background: ${darkMode ? 'rgba(255,255,255,0.04)' : '#f8faff'} !important; }
        .cx-action-row { transition: background 0.15s; }
        .cx-nav-pill:hover { opacity: 0.85; }
        @keyframes cx-fadein { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:translateY(0);} }
        .cx-fadein { animation: cx-fadein 0.3s ease forwards; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }} className="cx-fadein">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>
            {greeting}, <span style={{ color: '#f97316' }}>{user?.full_name?.split(' ')[0] || 'there'}</span> 👋
          </h1>
          <p style={{ fontSize: 12, color: T.text3, margin: '3px 0 0' }}>{todayLabel}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Staff online badge */}
          <div
            onClick={() => role === 'admin' && setShowOfficeLog(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: darkMode ? 'rgba(16,185,129,0.15)' : '#f0fdf4', border: `1px solid ${darkMode ? 'rgba(16,185,129,0.3)' : '#bbf7d0'}`, borderRadius: 20, padding: '5px 12px', cursor: role === 'admin' ? 'pointer' : 'default' }}
            title={role === 'admin' ? 'Click to view login/logout history' : (hrData.logged_in_today?.map(u => u.full_name).join(', ') || 'No one logged in')}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', display: 'block', boxShadow: '0 0 0 2px rgba(16,185,129,0.3)', animation: 'cx-pulse 2s infinite' }}></span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>{hrData.logged_in_today?.length || 0} in office</span>
          </div>
          {/* Role badge */}
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 10px', borderRadius: 20, background: role === 'admin' ? 'rgba(99,102,241,0.12)' : role === 'manager' ? 'rgba(59,130,246,0.12)' : 'rgba(107,114,128,0.12)', color: role === 'admin' ? '#6366f1' : role === 'manager' ? '#3b82f6' : '#6b7280' }}>
            {role}
          </div>
          {/* Quick nav pills */}
          {[
            { label: '+ Lead', page: 'crm', color: '#6366f1' },
            { label: '+ Client', page: 'clients', color: '#3b82f6' },
            { label: '+ Task', page: 'tasks', color: '#f59e0b' },
          ].map(a => (
            <button key={a.label} onClick={() => setActivePage(a.page)} className="cx-nav-pill"
              style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${a.color}`, background: 'transparent', color: a.color, cursor: 'pointer', transition: 'opacity 0.15s' }}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI Strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kpiCards.length}, 1fr)`, gap: 12, marginBottom: 20 }} className="cx-fadein">
        {kpiCards.map((card) => (
          <button key={card.label} onClick={() => setActivePage(card.page)} className="cx-dash-kpi"
            style={{ ...C.card, padding: '16px 18px', textAlign: 'left', border: 'none', cursor: 'pointer', position: 'relative', overflow: 'hidden', boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
            {/* Left accent bar */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: card.accent, borderRadius: '14px 0 0 14px' }}></div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: darkMode ? `rgba(${hexToRgb(card.accent)},0.15)` : `${card.accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={card.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d={card.icon}/>
                </svg>
              </div>
            </div>
            <p style={{ fontSize: 22, fontWeight: 800, color: card.accent, margin: 0, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{card.value}</p>
            <p style={{ fontSize: 11, fontWeight: 600, color: T.text2, margin: '4px 0 2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</p>
            <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{card.sub}</p>
          </button>
        ))}
      </div>

      {/* ── Main Grid: Chart + Tasks Progress | Action Items ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 16 }} className="cx-fadein">

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Finance Chart — Admin/Manager only */}
          {(role === 'admin' || role === 'manager') && (
            <div style={{ ...C.card, padding: '18px 20px', boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Billing vs Collections</h3>
                  <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>Monthly trend</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 12, marginRight: 8 }}>
                    {[{ color: '#6366f1', label: 'Billed' }, { color: '#10b981', label: 'Collected' }].map(l => (
                      <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }}></div>
                        <span style={{ fontSize: 11, color: T.text3 }}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                  {/* Period selector */}
                  <div style={{ display: 'flex', background: T.bg3, borderRadius: 8, padding: 2, gap: 2 }}>
                    {[{ label: '3M', val: 3 }, { label: '6M', val: 6 }, { label: '12M', val: 12 }].map(f => (
                      <button key={f.val} onClick={() => setFinanceMonths(f.val)}
                        style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', transition: 'all 0.15s', background: financeMonths === f.val ? T.cardBg : 'transparent', color: financeMonths === f.val ? '#6366f1' : T.text3, boxShadow: financeMonths === f.val ? (darkMode ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.1)') : 'none' }}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ position: 'relative', height: 200 }}>
                <canvas ref={chartRef} role="img" aria-label="Monthly billing vs collections bar chart"></canvas>
              </div>
            </div>
          )}

          {/* Task Progress */}
          <div style={{ ...C.card, padding: '18px 20px', boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Task Progress</h3>
                <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>
                  {stats?.tasks?.completed ?? 0} of {stats?.tasks?.total ?? 0} completed · {completedPct}%
                </p>
              </div>
              <button onClick={() => setActivePage('tasks')} style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                View all →
              </button>
            </div>
            {/* Segmented bar */}
            <div style={{ height: 10, borderRadius: 6, background: T.bg3, overflow: 'hidden', display: 'flex', gap: 1, marginBottom: 12 }}>
              {[
                { val: stats?.tasks?.completed, color: '#10b981' },
                { val: stats?.tasks?.in_progress, color: '#6366f1' },
                { val: stats?.tasks?.pending, color: '#f59e0b' },
                { val: stats?.tasks?.hold, color: '#94a3b8' },
                { val: stats?.tasks?.overdue, color: '#ef4444' },
              ].filter(s => s.val > 0).map((seg, i) => (
                <div key={i} style={{ height: '100%', background: seg.color, width: `${Math.max((seg.val / (stats?.tasks?.total || 1)) * 100, 1)}%`, borderRadius: i === 0 ? '6px 0 0 6px' : 0 }}></div>
              ))}
            </div>
            {/* Stat row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
              {[
                { label: 'Done', value: stats?.tasks?.completed ?? 0, color: '#10b981' },
                { label: 'Active', value: stats?.tasks?.in_progress ?? 0, color: '#6366f1' },
                { label: 'Pending', value: stats?.tasks?.pending ?? 0, color: '#f59e0b' },
                { label: 'Hold', value: stats?.tasks?.hold ?? 0, color: '#94a3b8' },
                { label: 'Overdue', value: stats?.tasks?.overdue ?? 0, color: '#ef4444' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '8px 0', borderRadius: 8, background: T.bg3 }}>
                  <p style={{ fontSize: 18, fontWeight: 800, color: s.color, margin: 0, lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0', fontWeight: 600 }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Recent pending tasks mini-list */}
            {pendingTasks.length > 0 && (
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>Recent Pending</p>
                {pendingTasks.slice(0, 4).map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</p>
                      <p style={{ fontSize: 11, color: T.text3, margin: '1px 0 0' }}>{t.client_name || 'No client'}{t.assigned_name ? ` · ${t.assigned_name}` : ''}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8, flexShrink: 0 }}>
                      {t.due_date && (
                        <span style={{ fontSize: 11, color: new Date(t.due_date) < now ? '#ef4444' : T.text3, fontWeight: new Date(t.due_date) < now ? 700 : 400 }}>{fmtDate(t.due_date)}</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: t.priority === 'urgent' ? '#fef2f2' : t.priority === 'high' ? '#fff7ed' : darkMode ? T.bg3 : '#f1f5f9', color: t.priority === 'urgent' ? '#ef4444' : t.priority === 'high' ? '#f97316' : T.text3 }}>
                        {t.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Staff Activity — Admin only */}
          {role === 'admin' && hrData.logged_in_today?.length > 0 && (
            <div style={{ ...C.card, padding: '18px 20px', boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Staff in Office Today</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setShowOfficeLog(true)} style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                    View Log
                  </button>
                  <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700, background: darkMode ? 'rgba(16,185,129,0.15)' : '#f0fdf4', padding: '3px 10px', borderRadius: 12 }}>
                    {hrData.logged_in_today.length} present
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {hrData.logged_in_today.map((u, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 20, background: T.bg3, border: `1px solid ${T.border}` }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: u.avatar_url ? 'transparent' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, overflow: 'hidden' }}>
                      {u.avatar_url
                        ? <img src={`${BASE}${u.avatar_url}`} alt={u.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (u.full_name?.charAt(0)?.toUpperCase() || 'U')}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{u.full_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column — Action Items */}
        <div style={{ ...C.card, padding: 0, overflow: 'hidden', boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Today's Actions</h3>
              <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>Overdue · Renewals · Outstanding</p>
            </div>
            {actionItems.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, background: '#fef2f2', color: '#ef4444' }}>
                {actionItems.length} items
              </span>
            )}
          </div>

          {/* Recent Leads section */}
          <div style={{ padding: '10px 16px 6px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Recent Leads</p>
            {recentLeads.length === 0 ? (
              <p style={{ fontSize: 12, color: T.text3, padding: '4px 0 8px' }}>No recent leads</p>
            ) : recentLeads.slice(0, 3).map(lead => (
              <div key={lead.id} className="cx-action-row" onClick={() => setActivePage('crm')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company || lead.name}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{lead.phone}</p>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: lead.stage === 'won' ? '#f0fdf4' : lead.stage === 'new' ? '#eef2ff' : darkMode ? T.bg3 : '#f1f5f9', color: lead.stage === 'won' ? '#16a34a' : lead.stage === 'new' ? '#6366f1' : T.text2, textTransform: 'capitalize', flexShrink: 0, marginLeft: 8 }}>
                  {lead.stage?.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>

          {/* Action items list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
            {actionItems.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', color: T.text3 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 8, opacity: 0.5 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>All clear!</p>
                <p style={{ fontSize: 11, margin: '3px 0 0' }}>No urgent items</p>
              </div>
            ) : actionItems.map((item, i) => (
              <div key={i} className="cx-action-row" onClick={() => setActivePage(item.page)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}>
                {/* Urgency dot */}
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.badgeColor, flexShrink: 0 }}></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{item.sub}</p>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${item.badgeColor}18`, color: item.badgeColor, flexShrink: 0 }}>
                  {item.badge}
                </span>
              </div>
            ))}
          </div>

          {/* Recent Payments footer */}
          {recentPayments.length > 0 && (
            <div style={{ padding: '10px 16px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Recent Receipts</p>
              {recentPayments.slice(0, 2).map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name}</p>
                    <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{p.invoice_number}</p>
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 800, color: '#10b981', margin: 0, flexShrink: 0, marginLeft: 8 }}>{fmtCurr(p.amount)}</p>
                </div>
              ))}
              <button onClick={() => setActivePage('finance')} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '6px 0 0', display: 'block' }}>
                View Finance →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Module Snapshot Tiles ── */}
      <div style={{ marginBottom: 8 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Module Overview</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
          {moduleTiles.map(tile => (
            <button key={tile.key} onClick={() => setActivePage(tile.page)} className="cx-tile"
              style={{ ...C.card, padding: '14px 16px', textAlign: 'left', border: `1px solid ${T.cardBorder}`, cursor: 'pointer', background: darkMode ? tile.darkBg : tile.lightBg, boxShadow: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${tile.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tile.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={tile.icon}/>
                  </svg>
                </div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </div>
              <p style={{ fontSize: 20, fontWeight: 800, color: tile.accent, margin: 0, lineHeight: 1 }}>{tile.value}</p>
              <p style={{ fontSize: 10, color: T.text2, margin: '3px 0 1px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tile.valueLabel}</p>
              <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{tile.sub}</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: tile.accent, margin: '8px 0 0' }}>{tile.label} →</p>
            </button>
          ))}
        </div>
      </div>
    </div>

    {showOfficeLog && (
      <UserOfficeLogModal darkMode={darkMode} T={T} onClose={() => setShowOfficeLog(false)} />
    )}
    </>
  );
}

// ─── Hex to RGB helper for KPI icon bg ───────────────────────────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ─── User Office Log Modal (Admin) ──────────────────────────────────────────
// Click "N in office" → pick a user → see their login/logout history,
// filterable by day or month.
function UserOfficeLogModal({ darkMode, T, onClose }) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [period, setPeriod] = useState('day');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers }).then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUser) return;
    setLoadingLogs(true);
    fetch(`${BASE}/api/dashboard/login-history?user_id=${selectedUser.id}&period=${period}&date=${date}`, { headers })
      .then(r => r.json())
      .then(d => setLogs(Array.isArray(d) ? d : []))
      .catch(() => setLogs([]))
      .finally(() => setLoadingLogs(false));
  }, [selectedUser, period, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDT = (v) => v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDur = (mins) => mins == null ? 'Active' : mins >= 60 ? `${Math.floor(mins/60)}h ${mins%60}m` : `${mins}m`;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', zIndex: 1000, transform: 'translate(-50%, -50%)',
        width: 720, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        background: darkMode ? T.bg2 : '#fff', borderRadius: 16, boxShadow: '0 32px 80px rgba(0,0,0,0.25)', overflow: 'hidden'
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>
              {selectedUser ? (
                <>
                  <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, marginRight: 8 }}>← Back</button>
                  {selectedUser.full_name}'s Login/Logout Log
                </>
              ) : 'Staff Office Log'}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: T.text3, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {!selectedUser ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {users.map(u => (
                <button key={u.id} onClick={() => setSelectedUser(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 20, background: T.bg3, border: `1px solid ${T.border}`, cursor: 'pointer' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: u.avatar_url ? 'transparent' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, overflow: 'hidden' }}>
                    {u.avatar_url
                      ? <img src={`${BASE}${u.avatar_url}`} alt={u.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : (u.full_name?.charAt(0)?.toUpperCase() || 'U')}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{u.full_name}</span>
                  <span style={{ fontSize: 11, color: T.text3, textTransform: 'capitalize' }}>{u.role}</span>
                </button>
              ))}
              {users.length === 0 && <p style={{ fontSize: 13, color: T.text3 }}>No users found.</p>}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                  {['day', 'month'].map(p => (
                    <button key={p} onClick={() => setPeriod(p)}
                      style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textTransform: 'capitalize', background: period === p ? '#6366f1' : 'transparent', color: period === p ? '#fff' : T.text2 }}>
                      {p}
                    </button>
                  ))}
                </div>
                <input type={period === 'month' ? 'month' : 'date'}
                  value={period === 'month' ? date.slice(0, 7) : date}
                  onChange={(e) => setDate(period === 'month' ? `${e.target.value}-01` : e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 12, background: T.bg, color: T.text }} />
              </div>

              {loadingLogs ? (
                <p style={{ fontSize: 13, color: T.text3 }}>Loading…</p>
              ) : logs.length === 0 ? (
                <p style={{ fontSize: 13, color: T.text3 }}>No login activity for this {period}.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: T.text3, borderBottom: `1px solid ${T.border}` }}>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Login</th>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Logout</th>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '8px', color: T.text }}>{fmtDT(l.login_time)}</td>
                        <td style={{ padding: '8px', color: T.text }}>{fmtDT(l.logout_time)}</td>
                        <td style={{ padding: '8px', color: l.duration_minutes == null ? '#10b981' : T.text2, fontWeight: l.duration_minutes == null ? 700 : 400 }}>{fmtDur(l.duration_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}



// ─── Bell Alert Modal ─────────────────────────────────────────────────────────
function BellAlertModal({ notifications, onGotIt, onClose }) {
  return (
    <>
      <style>{`
        @keyframes badge-blink {
          0%,100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.3); opacity: 0.7; }
        }
        @keyframes bell-ring {
          0%   { transform: rotate(0deg); }
          10%  { transform: rotate(18deg); }
          25%  { transform: rotate(-16deg); }
          40%  { transform: rotate(12deg); }
          55%  { transform: rotate(-10deg); }
          70%  { transform: rotate(6deg); }
          85%  { transform: rotate(-4deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes modal-pop {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          60%  { transform: translate(-50%, -50%) scale(1.03); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes backdrop-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 999, animation: 'backdrop-fade 0.25s ease'
        }} />

      {/* Modal card */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', zIndex: 1000,
        transform: 'translate(-50%, -50%)',
        animation: 'modal-pop 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards',
        width: 380, maxWidth: '90vw',
        background: '#fff', borderRadius: 20,
        boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
        overflow: 'hidden'
      }}>
        {/* Colored top band */}
        <div style={{ background: 'linear-gradient(135deg,#1d3461,#1f6feb)', padding: '28px 24px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, animation: 'bell-ring 0.7s ease-in-out 3', display: 'inline-block', lineHeight: 1 }}>🔔</div>
          <p style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginTop: 10 }}>
            {notifications.every(n => n.type === 'task_mention') ? "You've Been Mentioned" : 'New Notification'}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 4 }}>
            You have {notifications.length} new item{notifications.length > 1 ? 's' : ''} requiring your attention
          </p>
        </div>

        {/* Notification list */}
        <div style={{ maxHeight: 240, overflowY: 'auto', padding: '12px 16px' }}>
          {notifications.slice(0, 5).map(n => (
            <div key={n.id} style={{
              padding: '10px 12px', borderRadius: 10, marginBottom: 8,
              background: '#f0f5ff', border: '1px solid #dbeafe'
            }}>
              <p style={{ fontWeight: 600, fontSize: 13, color: '#1e40af', marginBottom: 2 }}>{n.title}</p>
              {n.message && <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>{n.message}</p>}
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                {new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          ))}
          {notifications.length > 5 && (
            <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
              +{notifications.length - 5} more notifications
            </p>
          )}
        </div>

        {/* Footer button */}
        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb',
              background: '#f9fafb', color: '#6b7280', fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}>
            View Later
          </button>
          <button
            onClick={onGotIt}
            style={{
              flex: 2, padding: '10px 0', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#1d3461,#1f6feb)',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(31,111,235,0.4)'
            }}>
            ✓ Got it — Mark as Read
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Notification Panel (dropdown) ────────────────────────────────────────────
function NotificationPanel({ notifications, unreadCount, onMarkAllRead, onMarkOneRead, onClearAll, onClose, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9' };
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const typeIcon = (type) => {
    if (type === 'task_assignment') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></svg>
    );
    if (type === 'task_update') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    );
    if (type === 'reminder') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    );
  };

  const typeBg = (type) => {
    if (type === 'task_assignment') return { bg:darkMode?'rgba(59,130,246,0.15)':'#eff6ff',  border:darkMode?'rgba(59,130,246,0.3)':'#bfdbfe' };
    if (type === 'task_update')    return { bg:darkMode?'rgba(245,158,11,0.15)':'#fffbeb',  border:darkMode?'rgba(245,158,11,0.3)':'#fde68a' };
    if (type === 'reminder')       return { bg:darkMode?'rgba(139,92,246,0.15)':'#f5f3ff',  border:darkMode?'rgba(139,92,246,0.3)':'#ddd6fe' };
    return                                { bg:T.bg3,                                        border:T.border };
  };

  const readBg    = darkMode ? T.bg2  : '#fff';
  const unreadBg  = darkMode ? 'rgba(59,130,246,0.08)' : '#f8faff';
  const readHover = darkMode ? T.bg3  : '#f9fafb';
  const unreadHover = darkMode ? 'rgba(59,130,246,0.14)' : '#eff6ff';

  return (
    <div ref={panelRef} style={{
      position:'fixed', top:56, right:16,
      width:360, maxHeight:'calc(100vh - 80px)',
      display:'flex', flexDirection:'column',
      background:T.bg2, borderRadius:16,
      boxShadow: darkMode?'0 20px 60px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3)':'0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
      border:`1px solid ${T.border}`, zIndex:9999, overflow:'hidden'
    }}>
      {/* Header — always navy, looks good in both modes */}
      <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'16px 16px 0 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <span style={{ fontWeight:700, fontSize:14, color:'#fff' }}>Notifications</span>
          {unreadCount > 0 && (
            <span style={{ background:'#ef4444', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10 }}>{unreadCount} new</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {unreadCount > 0 && (
            <button onClick={onMarkAllRead}
              style={{ fontSize:12, color:'rgba(255,255,255,0.7)', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontWeight:500 }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.2)'; e.currentTarget.style.color='#fff'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={onClearAll}
              style={{ fontSize:12, color:'rgba(255,100,100,0.85)', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontWeight:500 }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(239,68,68,0.25)'; e.currentTarget.style.color='#fca5a5'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(239,68,68,0.15)'; e.currentTarget.style.color='rgba(255,100,100,0.85)'; }}>
              Clear all
            </button>
          )}
          <button onClick={onClose}
            style={{ width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.2)'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ overflowY:'auto', flex:1, scrollbarWidth:'none', msOverflowStyle:'none' }}>
        {notifications.length === 0 ? (
          <div style={{ padding:'48px 16px', textAlign:'center' }}>
            <div style={{ width:48, height:48, borderRadius:12, background:T.bg3, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </div>
            <p style={{ fontSize:13, fontWeight:500, color:T.text2, margin:0 }}>All caught up!</p>
            <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>No notifications yet</p>
          </div>
        ) : notifications.map(n => {
          const tb = typeBg(n.type);
          return (
            <div key={n.id}
              style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, background:n.is_read?readBg:unreadBg, transition:'background 0.15s', cursor:'default' }}
              onMouseEnter={e=>e.currentTarget.style.background=n.is_read?readHover:unreadHover}
              onMouseLeave={e=>e.currentTarget.style.background=n.is_read?readBg:unreadBg}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:32, height:32, borderRadius:8, background:tb.bg, border:`1px solid ${tb.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {typeIcon(n.type)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontWeight:n.is_read?500:600, fontSize:13, color:T.text, margin:0, lineHeight:1.4 }}>{n.title}</p>
                  {n.message && <p style={{ fontSize:12, color:T.text2, margin:'3px 0 0', lineHeight:1.4 }}>{n.message}</p>}
                  <p style={{ fontSize:11, color:T.text3, margin:'5px 0 0' }}>
                    {new Date(n.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </p>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6, flexShrink:0 }}>
                  {!n.is_read && (
                    <>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'#3b82f6', display:'block' }}/>
                      <button onClick={() => onMarkOneRead(n.id)}
                        style={{ fontSize:10, color:'#3b82f6', background:darkMode?'rgba(59,130,246,0.15)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, borderRadius:5, padding:'2px 7px', cursor:'pointer', fontWeight:600, whiteSpace:'nowrap' }}
                        onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.25)':'#dbeafe'}
                        onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.15)':'#eff6ff'}>
                        Mark read
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div style={{ padding:'10px 16px', borderTop:`1px solid ${T.border}`, textAlign:'center', background:T.bg3 }}>
          <span style={{ fontSize:12, color:T.text3 }}>{notifications.length} notification{notifications.length!==1?'s':''} total</span>
        </div>
      )}
    </div>
  );
}

// ─── Coming Soon ──────────────────────────────────────────────────────────────
function ComingSoon({ title, desc, icon }) {
  return (
    <div className="flex items-center justify-center h-full min-h-96">
      <div className="text-center">
        <div className="text-6xl mb-4">{icon}</div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0f1f45' }}>{title}</h2>
        <p className="text-gray-400">{desc}</p>
        <div className="mt-4 px-4 py-2 rounded-full text-xs font-medium bg-blue-50 text-blue-600 inline-block">
          🚧 Under Development
        </div>
      </div>
    </div>
  );
}
// ─── Avatar Dropdown Menu ─────────────────────────────────────────────────────
function AvatarMenu({ user, onLogout, darkMode, T: theme }) {
  const T = theme || {
    bg2: '#fff', border: '#e2e8f0', text: '#0f1f45', text2: '#475569', text3: '#94a3b8',
    cardBg: '#fff', cardBorder: '#f1f5f9',
  };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
        title={user?.full_name}
      >
        <span className="text-sm text-gray-500 hidden md:block">{user?.full_name}</span>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: user?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #1d3461, #1f6feb)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14, fontWeight: 700, overflow: 'hidden',
          boxShadow: open ? '0 0 0 3px rgba(59,130,246,0.3)' : 'none',
          transition: 'box-shadow 0.2s',
        }}>
          {user?.avatar_url
            ? <img src={`${BASE}${user.avatar_url}`} alt={user.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : user ? user.full_name.charAt(0).toUpperCase() : 'U'}
        </div>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          width: 220, background: T.bg2, borderRadius: 12,
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(0,0,0,0.15)',
          border: `1px solid ${T.border}`, zIndex: 200, overflow: 'hidden',
        }}>
          {/* User info header */}
          <div style={{
            padding: '14px 16px 12px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(135deg, #060d1f, #0a1628)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: user?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #1d3461, #1f6feb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 15, fontWeight: 700, flexShrink: 0, overflow: 'hidden',
              }}>
                {user?.avatar_url
                  ? <img src={`${BASE}${user.avatar_url}`} alt={user.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : user ? user.full_name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: 13, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.full_name}
                </p>
                <p style={{ color: '#93c5fd', fontSize: 11, margin: '2px 0 0', textTransform: 'capitalize' }}>
                  {user?.role}
                </p>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div style={{ padding: '6px' }}>
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, border: 'none',
                background: 'none', cursor: 'pointer', color: '#ef4444',
                fontSize: 13, fontWeight: 500, transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default Dashboard;